import { isEmpty } from 'lodash';
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { getConfig } from 'src/global-config';
import { createRedirectPath } from 'src/utils';

import { SplashScreen } from 'src/components/loading-screen';

import { useHRAuth } from 'src/auth/hooks/use-hr-context';

type Props = {
  children: React.ReactNode;
};

export function HRAuthGuard({ children }: Props) {
  const CONFIG = getConfig();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isChecking, setIsChecking] = useState(true);
  const { isAuthenticated } = useAuth0();

  const { loadingHRAuth, Token, EmailConfirmed, IsUserOnboarded } = useHRAuth();
  const { method } = CONFIG.auth;

  const fullPath = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  const validate = () => {
    
    // Wait Until 
    if (loadingHRAuth) {
      return;
    }

    if (!isAuthenticated) {
      router.replace(createRedirectPath(paths.auth[method].signIn, fullPath));
      return;
    }

    if (
      EmailConfirmed === undefined ||
      EmailConfirmed === null ||
      IsUserOnboarded === undefined ||
      IsUserOnboarded === null
    ) {
      return;
    }

    // Now we have valid data from the HR provider, make routing decisions
    if (isEmpty(Token)) {
      if (!EmailConfirmed) {
        router.replace(
          createRedirectPath((paths.auth[method] as typeof paths.auth.auth0).verifyEmail, fullPath)
        );
        return;
      }

      if (!IsUserOnboarded) {
        router.replace(
          createRedirectPath((paths.auth[method] as typeof paths.auth.auth0).onBoarding, fullPath)
        );
        return;
      }
    }

    setIsChecking(false);
  };

  useEffect(() => {
    validate();
  }, [isAuthenticated, loadingHRAuth, Token, EmailConfirmed, IsUserOnboarded]);

  if (isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
